// Direct-to-S3 upload with a pre-signed POST (requirement 5.3; design 4.4).
import { ALLOWED_CONTENT_TYPES, MAX_UPLOAD_BYTES, TOO_LARGE_MESSAGE, type AllowedContentType } from '@bda/shared';

export interface PresignedPost {
  url: string;
  fields: Record<string, string>;
}

const EXT_TYPES: Record<string, AllowedContentType> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  tif: 'image/tiff',
  tiff: 'image/tiff',
};

/** Browsers sometimes leave File.type empty for TIFF; fall back to the extension. */
export function contentTypeOf(file: File): AllowedContentType | null {
  if ((ALLOWED_CONTENT_TYPES as readonly string[]).includes(file.type)) return file.type as AllowedContentType;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return EXT_TYPES[ext] ?? null;
}

/** Type and size checks (synchronous). The page limit is checked by `useFileCheck` in lib/pageCount.ts. */
export function validateFile(file: File): string | null {
  if (!contentTypeOf(file)) return 'Upload a PDF, PNG, JPEG, or TIFF file.';
  if (file.size > MAX_UPLOAD_BYTES) return TOO_LARGE_MESSAGE;
  if (file.size === 0) return 'The file is empty.';
  return null;
}

export function uploadToS3(post: PresignedPost, file: File, onProgress?: (fraction: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    for (const [k, v] of Object.entries(post.fields)) form.append(k, v);
    form.append('file', file);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', post.url);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status}).`));
    xhr.onerror = () => reject(new Error('Upload failed. Check your connection and try again.'));
    xhr.send(form);
  });
}
